import { ProFormSwitch, ProFormText, ProFormDigit } from '@ant-design/pro-components';
import { ModalForm } from '@ant-design/pro-components';
import type { ModalFormProps } from '@ant-design/pro-components';
import { Form, Input, Space } from 'antd';
import type { FormInstance } from 'antd';
import React, { useEffect, useState } from 'react';
import { useIntl } from '@umijs/max';
import { DisplayTextPreviewButton, AssetImageUpload } from '@/components';
import type { AssetImageSpec, ExtraAssetImage } from '@/components/AssetImageUpload';

export type DictionaryFormValueType = Omit<API.DictionaryCreateRequest, 'type'>;

export interface DictionaryFormProps {
  /** 是否为编辑模式（默认 false，即创建模式） */
  isEdit?: boolean;
  /** 编辑模式下的初始值 */
  initialValues?: API.DictionaryResponse;
  /** 字典类型 */
  type: API.DictionaryType;
  /** 父级ID（可选，传 null 表示根节点） */
  parentId?: string | null;
  /** 是否显示父级ID字段（默认 false） */
  showParentId?: boolean;
  /** 是否显示排序字段（默认 true） */
  showSortOrder?: boolean;
  /** 是否显示启用状态字段（默认 true） */
  showIsActive?: boolean;
  /** 是否显示扩展字段（默认 false） */
  showExtra?: boolean;
  /** 配图规格：设置后显示 1:1 裁剪并生成 1x/2x 上传，写入 extra.assets；不设置则不展示配图 */
  imageAssetConfig?: AssetImageSpec;
  /** 是否显示多语言显示文本字段（默认 true） */
  showDisplayText?: boolean;
  /** Modal 标题（会根据 isEdit 自动生成，也可自定义） */
  title?: string;
  /** value 字段的标签（默认根据 type 自动生成） */
  valueLabel?: string;
  /** value 字段的占位符（默认根据 type 自动生成） */
  valuePlaceholder?: string;
  /** value 字段的提示文本 */
  valueTooltip?: string;
  /** 多语言显示文本的标签（默认 "多语言显示文本"） */
  displayTextLabel?: string;
  /** 多语言显示文本的提示文本 */
  displayTextTooltip?: string;
  /** 是否必填 display_text（默认 false，即可选） */
  displayTextRequired?: boolean;
  /** Modal 的其他属性 */
  modalProps?: ModalFormProps;
  /** 表单打开状态 */
  open: boolean;
  /** 取消回调 */
  onCancel: () => void;
  /** 提交回调 */
  onSubmit: (values: DictionaryFormValueType | API.DictionaryUpdateRequest) => Promise<void>;
  /** 自定义渲染函数，可以在表单字段之间插入自定义内容 */
  renderCustomFields?: (form: FormInstance) => React.ReactNode;
}

/**
 * 通用字典项表单组件
 * 
 * 支持创建和编辑两种模式，提供丰富的配置参数以满足不同场景需求。
 * 
 * @example
 * ```tsx
 * // 创建模式 - 基础用法
 * <DictionaryForm
 *   type="job_position"
 *   open={modalVisible}
 *   onCancel={() => setModalVisible(false)}
 *   onSubmit={handleSubmit}
 * />
 * 
 * // 创建模式 - 带父级
 * <DictionaryForm
 *   type="job_location"
 *   parentId={parentId}
 *   showParentId={true}
 *   open={modalVisible}
 *   onCancel={() => setModalVisible(false)}
 *   onSubmit={handleSubmit}
 * />
 * 
 * // 编辑模式
 * <DictionaryForm
 *   isEdit={true}
 *   type="job_position"
 *   initialValues={currentRow}
 *   open={updateModalVisible}
 *   onCancel={() => setUpdateModalVisible(false)}
 *   onSubmit={handleUpdate}
 * />
 * 
 * // 自定义字段
 * <DictionaryForm
 *   type="job_position"
 *   open={modalVisible}
 *   onCancel={() => setModalVisible(false)}
 *   onSubmit={handleSubmit}
 *   renderCustomFields={(form) => (
 *     <ProFormText name="custom_field" label="自定义字段" />
 *   )}
 * />
 * ```
 */
const DictionaryForm: React.FC<DictionaryFormProps> = (props) => {
  const {
    isEdit = false,
    initialValues,
    type,
    parentId,
    showParentId = false,
    showSortOrder = true,
    showIsActive = true,
    showExtra = false,
    imageAssetConfig,
    showDisplayText = true,
    title,
    valueLabel,
    valuePlaceholder,
    valueTooltip,
    displayTextLabel = '多语言显示文本',
    displayTextTooltip = '可选，用于多语言场景。可以手动填写或使用自动生成功能',
    displayTextRequired = false,
    modalProps,
    open,
    onCancel,
    onSubmit,
    renderCustomFields,
  } = props;

  const intl = useIntl();
  const [form] = Form.useForm();
  const [displayText, setDisplayText] = useState<Record<string, string>>({});
  /** 配图资源（extra.assets[0]），仅当 imageAssetConfig 存在时使用 */
  const [imageAsset, setImageAsset] = useState<ExtraAssetImage | null>(null);

  // 根据 type 生成默认的标签和占位符
  const getDefaultLabels = () => {
    const labels: Record<string, { label: string; placeholder: string }> = {
      job_position: { label: '标签名称', placeholder: '请输入标签名称，例如：软件工程师' },
      job_location: { label: '地点名称', placeholder: '请输入地点名称，例如：北京' },
      company_size: { label: '企业规模', placeholder: '请输入企业规模，例如：1-50人' },
      industry: { label: '行业名称', placeholder: '请输入行业名称，例如：互联网、制造业、服务业' },
      ad_channel_facebook: { label: '层级名称', placeholder: '请输入层级名称' },
      ad_channel_tiktok: { label: '层级名称', placeholder: '请输入层级名称' },
      ad_channel_google: { label: '层级名称', placeholder: '请输入层级名称' },
    };
    return labels[type] || { label: '名称', placeholder: '请输入名称' };
  };

  const defaultLabels = getDefaultLabels();
  const finalValueLabel = valueLabel || defaultLabels.label;
  const finalValuePlaceholder = valuePlaceholder || defaultLabels.placeholder;
  const finalValueTooltip = valueTooltip || '字典项的真实含义/字面量，建议统一用中文填写';

  // 生成默认标题
  const getDefaultTitle = () => {
    if (isEdit) {
      return `编辑${finalValueLabel}`;
    }
    return `新建${finalValueLabel}`;
  };

  const finalTitle = title || getDefaultTitle();

  // 编辑模式下初始化表单值
  useEffect(() => {
    if (!open) {
      // 关闭时重置
      return;
    }

    if (!imageAssetConfig) {
      setImageAsset(null);
    }

    if (isEdit && initialValues) {
      form.setFieldsValue({
        value: initialValues.value,
        sort_order: initialValues.sort_order,
        is_active: initialValues.is_active,
        parent_id: initialValues.parent_id,
      });
      setDisplayText(initialValues.display_text || {});
      if (imageAssetConfig) {
        const first = initialValues.extra?.assets?.[0];
        setImageAsset(
          first && first.type === 'image' && first.url_2x
            ? { type: 'image', url_2x: first.url_2x, url_1x: first.url_1x }
            : null,
        );
      }
    } else if (!isEdit) {
      // 创建模式下重置表单
      form.resetFields();
      setDisplayText({});
      setImageAsset(null);
      if (parentId !== undefined) {
        form.setFieldsValue({ parent_id: parentId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, initialValues?.id, open, parentId, imageAssetConfig]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const buildExtra = (): Record<string, unknown> | undefined => {
        if (!imageAssetConfig) return undefined;
        const assets = imageAsset ? [imageAsset] : [];
        const base = isEdit && initialValues?.extra && typeof initialValues.extra === 'object' && !Array.isArray(initialValues.extra) ? (initialValues.extra as Record<string, unknown>) : {};
        return { ...base, assets };
      };

      // 编辑模式下的验证逻辑
      if (isEdit) {
        const submitValues: API.DictionaryUpdateRequest = {
          value: values.value,
          display_text: showDisplayText && Object.keys(displayText).length > 0 ? displayText : undefined,
          parent_id: showParentId && values.parent_id !== undefined ? (values.parent_id === '' ? null : values.parent_id) : undefined,
          sort_order: showSortOrder ? values.sort_order : undefined,
          is_active: showIsActive ? values.is_active : undefined,
          extra: imageAssetConfig ? buildExtra() : undefined,
        };
        await onSubmit(submitValues);
        return;
      }

      // 创建模式下的提交逻辑
      const submitValues: DictionaryFormValueType = {
        ...values,
        display_text: showDisplayText && Object.keys(displayText).length > 0 ? displayText : undefined,
        parent_id: parentId !== undefined ? (parentId || null) : (values.parent_id || null),
        sort_order: values.sort_order || 0,
        is_active: values.is_active !== false,
        extra: imageAssetConfig ? buildExtra() : undefined,
      };

      await onSubmit(submitValues);
      form.resetFields();
      setDisplayText({});
      setImageAsset(null);
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  const handleDisplayTextApply = (appliedDisplayText: Record<string, string>) => {
    setDisplayText(appliedDisplayText);
  };

  const handleCancel = () => {
    form.resetFields();
    setDisplayText({});
    setImageAsset(null);
    onCancel();
  };

  return (
    <ModalForm
      title={finalTitle}
      width="600px"
      form={form}
      open={open}
      onOpenChange={(visible) => {
        if (!visible) {
          handleCancel();
        }
      }}
      onFinish={handleSubmit}
      modalProps={{
        destroyOnClose: true,
        ...(modalProps || {}),
      } as any}
    >
      {showParentId && (
        <Form.Item label="父级ID" name="parent_id">
          <Input
            placeholder={isEdit ? '留空表示移除父级关联' : '留空表示根节点'}
            disabled={!isEdit && parentId !== undefined}
          />
        </Form.Item>
      )}

      {!isEdit && parentId !== undefined && (
        <Form.Item label="父级ID">
          <Input value={parentId || ''} disabled />
        </Form.Item>
      )}

      <ProFormText
        name="value"
        label={finalValueLabel}
        placeholder={finalValuePlaceholder}
        rules={[
          {
            required: true,
            message: `${finalValueLabel}是必填项`,
          },
          {
            max: 128,
            message: `${finalValueLabel}不能超过128个字符`,
          },
        ]}
        tooltip={finalValueTooltip}
      />

      {showDisplayText && (
        <Form.Item
          label={displayTextLabel}
          required={displayTextRequired}
          tooltip={displayTextTooltip}
        >
          <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.value !== currentValues.value}>
            {() => {
              const currentValue = form.getFieldValue('value') || '';
              const hasValue = currentValue?.trim();
              return (
                <div style={{ marginBottom: 8 }}>
                  <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
                    <Input
                      placeholder="中文名称（可选）"
                      value={displayText.zh_CN || ''}
                      onChange={(e) => {
                        setDisplayText({ ...displayText, zh_CN: e.target.value });
                      }}
                      style={{ flex: 1 }}
                    />
                    <DisplayTextPreviewButton
                      value={currentValue}
                      form={form}
                      onApply={handleDisplayTextApply}
                      buttonText="自动生成多语言"
                      buttonType={hasValue ? 'primary' : 'default'}
                      buttonSize="middle"
                      style={{ marginLeft: 8 }}
                    />
                  </Space.Compact>
                  <Input
                    placeholder="英文名称（可选）"
                    value={displayText.en_US || ''}
                    onChange={(e) => {
                      setDisplayText({ ...displayText, en_US: e.target.value });
                    }}
                    style={{ marginBottom: 8 }}
                  />
                  <Input
                    placeholder="高棉语名称（可选）"
                    value={displayText.km_KH || ''}
                    onChange={(e) => {
                      setDisplayText({ ...displayText, km_KH: e.target.value });
                    }}
                  />
                </div>
              );
            }}
          </Form.Item>
        </Form.Item>
      )}

      {imageAssetConfig && (
        <Form.Item
          label={intl.formatMessage({ id: '页面.配置.岗位标签.标签配图' })}
          tooltip={intl.formatMessage({ id: '页面.配置.岗位标签.标签配图tips' })}
        >
          <AssetImageUpload
            value={imageAsset}
            onChange={setImageAsset}
            spec={imageAssetConfig}
            placeholder={intl.formatMessage({ id: '页面.配置.岗位标签.选择图片并裁剪' })}
          />
        </Form.Item>
      )}

      {renderCustomFields?.(form)}

      {showSortOrder && (
        <ProFormDigit
          name="sort_order"
          label="排序顺序"
          placeholder="数字越小越靠前，默认为0（自动计算）"
          min={0}
          initialValue={0}
          tooltip="设置为0时，系统会自动计算排序值"
        />
      )}

      {showIsActive && (
        <ProFormSwitch
          name="is_active"
          label="是否启用"
          initialValue={true}
        />
      )}

      {showExtra && (
        <Form.Item label="扩展字段" name="extra">
          <Input.TextArea
            placeholder='JSON 格式，例如：{"key": "value"}'
            rows={4}
          />
        </Form.Item>
      )}
    </ModalForm>
  );
};

export default DictionaryForm;
